import React, { Fragment, useEffect } from 'react';
import styles from './styles.scss';

import {
  AppBar,
  AppBarContext,
  AppBarSection,
  AppBarTitle,
  Button,
  ButtonGroup,
  Checkbox,
  InputBox,
  Icons,
  RadioButton,
  Toaster,
} from '@densityco/ui';
import Popup from '../../../Popup';
import FormLabel from '../../../FormLabel';
import HorizontalForm from '../../../HorizontalForm';

import TriggerDataCaptureStore from '../../../../rx-stores/trigger-data-capture';
import {
  TriggerDataCaptureActionTypes,
  TriggerDataCaptureLength,
  TriggerDataCaptureStep,
} from '../../../../types/trigger-data-capture';
import { Sensor } from '../../../../types/api';
import { DurationUnit } from '../../../../types/durations';

import useRxStore from '../../../../utils/use-rx-store';
import useRxDispatch from '../../../../utils/use-rx-dispatch';
import { SenseiApi } from '../../../../utils/api';
import { supportsDenseCaptures } from '../../../../utils/sensor';
import { useRequiredSessionToken } from '../../../../utils/session-token';
import { ON_PREM } from '../../../../utils/environment';
import { commaSeparateList } from '../../../../utils/comma-separate';

type TriggerDataCaptureButtonProps = {
  sensors: Array<{
    serial_number: Sensor['serial_number'];
    os_version: Sensor['os_version'];
    diagnostic_info_json: Sensor['diagnostic_info_json'];
    in_mpi?: Sensor['in_mpi'];
  }>;
  onComplete?: () => void;
};

const INTEGER_REGEX = /^[0-9]+$/;

const TriggerDataCaptureButton: React.FunctionComponent<TriggerDataCaptureButtonProps> = props => {
  const { sensors, onComplete } = props;
  const accessToken = useRequiredSessionToken();

  const state = useRxStore(TriggerDataCaptureStore);
  const dispatch = useRxDispatch();

  const sensorsNotSupportingDenseCaptures = sensors.filter(
    i => !supportsDenseCaptures(i),
  );
  const sensorsInMPI = sensors.filter(
    i => i.in_mpi
  );
  const denseCapturesEnabled = sensorsNotSupportingDenseCaptures.length === 0 && sensorsInMPI.length === 0;

  const sensorsCurrentlyCapturingData = sensors.filter(i => {
    if (!i.diagnostic_info_json.capture_status) {
      return false;
    }
    const dataCollectionFinished = i.diagnostic_info_json.capture_status.finished;
    return !dataCollectionFinished;
  });

  const customLengthValid =
    state.step !== TriggerDataCaptureStep.INITIAL &&
    state.captureLength === TriggerDataCaptureLength.CUSTOM
      ? state.customCaptureLengthInSeconds.length > 0 &&
        INTEGER_REGEX.test(state.customCaptureLengthInSeconds) &&
        parseInt(state.customCaptureLengthInSeconds, 10) <= 600 // prevent >10m captures
      : true;

  const numberOfCapturesValid =
    state.step === TriggerDataCaptureStep.DENSE
      ? state.numberOfCaptures.length > 0 &&
        INTEGER_REGEX.test(state.numberOfCaptures)
      : true;

  const maxCaptureDurationValid =
    state.step === TriggerDataCaptureStep.DENSE
      ? state.maxCaptureDuration.length > 0 &&
        INTEGER_REGEX.test(state.maxCaptureDuration)
      : true;

  return (
    <Fragment>
      <Popup
        target={
          <Button
            disabled={sensors.length === 0}
            data-test-id="trigger-data-capture-button"
            rightIcon={<Icons.ChevronDown />}
          >
            Trigger Data Capture
          </Button>
        }
        open={state.open}
        onOpen={() => {
          if (state.open) {
            return;
          }
          dispatch({
            type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_OPEN,
            // Open directly into the "regular" form if the sensor is not running a new enough os
            // version for dense captures.
            openIntoRegularStep: ON_PREM || !denseCapturesEnabled,
          });
        }}
        onClose={() =>
          dispatch({
            type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CLOSE,
          })
        }
        position={{ right: 0, top: 44 }}
        noPadding
      >
        {state.step === TriggerDataCaptureStep.INITIAL ? (
          <Fragment>
            <AppBarContext.Provider value="CARD_HEADER">
              <AppBar>
                <AppBarTitle>Trigger Data Capture</AppBarTitle>
              </AppBar>
            </AppBarContext.Provider>

            {sensorsCurrentlyCapturingData.length > 0 ? (
              <div style={{margin: 16}}>
                <div
                  className={styles.warning}
                  data-test-id="trigger-data-capture-dense-capture-warning"
                >
                  <h3>Sensors currently collecting data</h3>
                  <p>
                    {commaSeparateList(
                      sensorsCurrentlyCapturingData.map(i => i.serial_number),
                    )}
                    {sensorsCurrentlyCapturingData.length === 1
                      ? ' is '
                      : ' are '}
                      currently capturing data. Triggering new data captures will interrupt data captures that are in progress.
                  </p>
                </div>
              </div>
            ) : null}


            <button
              className={styles.captureTypePickerItem}
              data-test-id="trigger-data-capture-regular"
              onClick={() =>
                dispatch({
                  type:
                    TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_STEP,
                  step: TriggerDataCaptureStep.REGULAR,
                })
              }
            >
              Regular Capture
              <Icons.ChevronRight />
            </button>
            <button
              className={styles.captureTypePickerItem}
              data-test-id="trigger-data-capture-dense"
              onClick={() =>
                dispatch({
                  type:
                    TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_STEP,
                  step: TriggerDataCaptureStep.DENSE,
                })
              }
            >
              Dense Capture
              <Icons.ChevronRight />
            </button>
          </Fragment>
        ) : (
          <Fragment>
            <AppBarContext.Provider value="CARD_HEADER">
              <AppBar>
                <AppBarSection>
                  {!ON_PREM && denseCapturesEnabled ? (
                    <Button
                      leftIcon={<Icons.ArrowLeft />}
                      width={40}
                      height={40}
                      onClick={() =>
                        dispatch({
                          type:
                            TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_STEP,
                          step: TriggerDataCaptureStep.INITIAL,
                        })
                      }
                    />
                  ) : null}
                  <span className={styles.title}>
                    Trigger{' '}
                    {state.step === TriggerDataCaptureStep.REGULAR
                      ? 'Regular'
                      : 'Dense'}{' '}
                    Data Capture
                  </span>
                </AppBarSection>
              </AppBar>
            </AppBarContext.Provider>

            <div
              className={styles.body}
              data-test-id="trigger-data-capture-body"
            >
              {!denseCapturesEnabled ? (
                <div
                  className={styles.warning}
                  data-test-id="trigger-data-capture-dense-capture-warning"
                >
                  <h3>Dense Captures Disabled</h3>
                  <p>
                    Dense captures are disabled:
                  </p>
                  <ul>
                    {sensorsNotSupportingDenseCaptures.length > 0 &&
                      <li><p>{commaSeparateList(
                        sensorsNotSupportingDenseCaptures.map(i => i.serial_number),
                      )}
                      {sensorsNotSupportingDenseCaptures.length === 1
                        ? ' is '
                        : ' are '}
                      not running an os release that is "Jamestown" or later.
                      </p></li>
                    }
                    {sensorsInMPI.length > 0 &&
                      <li><p>{commaSeparateList(
                        sensorsInMPI.map(i => i.serial_number),
                      )}
                      {sensorsInMPI.length === 1
                        ? ' is '
                        : ' are '}
                      in an MPI. Dense captures for MPIs are currently disabled.
                      </p></li>
                    }
                  </ul>
                </div>
              ) : null}

              {state.step === TriggerDataCaptureStep.REGULAR ||
              state.advancedControlsVisible ? (
                <FormLabel
                  label={
                    state.step === TriggerDataCaptureStep.DENSE
                      ? 'How long should a single capture be?'
                      : 'How long should the capture be?'
                  }
                  input={
                    <Fragment>
                      <div
                        className={styles.choice}
                        data-test-id="trigger-data-capture-short"
                      >
                        <RadioButton
                          text="Short (one minute long)"
                          checked={
                            state.captureLength ===
                            TriggerDataCaptureLength.SHORT
                          }
                          onChange={() =>
                            dispatch({
                              type:
                                TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_CAPTURE_LENGTH,
                              value: TriggerDataCaptureLength.SHORT,
                            })
                          }
                        />
                      </div>
                      <div
                        className={styles.choice}
                        data-test-id="trigger-data-capture-long"
                      >
                        <RadioButton
                          text="Long (three minutes long)"
                          checked={
                            state.captureLength ===
                            TriggerDataCaptureLength.LONG
                          }
                          onChange={() =>
                            dispatch({
                              type:
                                TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_CAPTURE_LENGTH,
                              value: TriggerDataCaptureLength.LONG,
                            })
                          }
                        />
                      </div>
                      <div
                        className={styles.choice}
                        data-test-id="trigger-data-capture-custom"
                      >
                        <RadioButton
                          text="Custom length"
                          checked={
                            state.captureLength ===
                            TriggerDataCaptureLength.CUSTOM
                          }
                          onChange={() =>
                            dispatch({
                              type:
                                TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_CAPTURE_LENGTH,
                              value: TriggerDataCaptureLength.CUSTOM,
                            })
                          }
                        />
                      </div>

                      {state.captureLength ===
                      TriggerDataCaptureLength.CUSTOM ? (
                        <div className={styles.customLength}>
                          <InputBox
                            type="text"
                            placeholder="ex: 30"
                            width={100}
                            value={state.customCaptureLengthInSeconds}
                            onChange={e =>
                              dispatch({
                                type:
                                  TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_CUSTOM_CAPTURE_LENGTH,
                                value: e.target.value,
                              })
                            }
                            data-test-id="trigger-data-capture-custom-value"
                          />
                          <span className={styles.unit}>seconds</span>
                        </div>
                      ) : null}
                    </Fragment>
                  }
                />
              ) : null}

              {state.step === TriggerDataCaptureStep.DENSE ? (
                <FormLabel
                  label="When should we stop capturing data?"
                  input={
                    <div>
                      <div className={styles.durationItemWrapper}>
                        <HorizontalForm>
                          <span>Capture data for</span>
                          <InputBox
                            type="text"
                            placeholder="ex: 5"
                            width={75}
                            value={state.maxCaptureDuration}
                            data-test-id="trigger-data-capture-max-capture-duration-value"
                            onChange={e =>
                              dispatch({
                                type:
                                  TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION,
                                value: e.target.value,
                              })
                            }
                          />
                          <div data-test-id="trigger-data-capture-max-capture-duration-unit">
                            <InputBox
                              type="select"
                              placeholder="Unit"
                              width={150}
                              choices={[
                                {
                                  id: DurationUnit.SECONDS,
                                  label:
                                    state.maxCaptureDuration === '1'
                                      ? 'second'
                                      : 'seconds',
                                },
                                {
                                  id: DurationUnit.MINUTES,
                                  label:
                                    state.maxCaptureDuration === '1'
                                      ? 'minute'
                                      : 'minutes',
                                },
                                {
                                  id: DurationUnit.HOURS,
                                  label:
                                    state.maxCaptureDuration === '1'
                                      ? 'hour'
                                      : 'hours',
                                },
                                {
                                  id: DurationUnit.DAYS,
                                  label:
                                    state.maxCaptureDuration === '1'
                                      ? 'day'
                                      : 'days',
                                },
                              ]}
                              value={state.maxCaptureDurationUnit}
                              onChange={c => {
                                dispatch({
                                  type:
                                    TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION_UNIT,
                                  value: c.id,
                                });
                              }}
                            />
                          </div>
                        </HorizontalForm>
                      </div>
                      <div className={styles.durationItemWrapper}>
                        <HorizontalForm>
                          <span>collecting at maximum</span>
                          <InputBox
                            placeholder="eg. 300"
                            width={100}
                            value={state.numberOfCaptures}
                            data-test-id="trigger-data-capture-number-of-captures"
                            onChange={e =>
                              dispatch({
                                type:
                                  TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_NUM_CAPTURES,
                                value: e.target.value,
                              })
                            }
                          />
                          <span>captures</span>
                        </HorizontalForm>
                      </div>
                    </div>
                  }
                />
              ) : null}

              <FormLabel
                label="Capture Options"
                input={
                  <Fragment>
                    <div className={styles.optionCheckboxWrapper}>
                      <Checkbox
                        checked={state.motion}
                        disabled={state.step === TriggerDataCaptureStep.DENSE}
                        label={<span>Require motion in capture</span>}
                        onChange={e =>
                          dispatch({
                            type:
                              TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MOTION,
                            checked: e.target.checked,
                          })
                        }
                      />
                    </div>
                    <div className={styles.optionCheckboxWrapper}>
                      <Checkbox
                        checked={state.motionDetectionTriggerLine}
                        disabled={!state.motion}
                        label={<span>Only capture trigger line motion</span>}
                        onChange={e =>
                          dispatch({
                            type:
                              TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MOTION_DETECTION_TRIGGER_LINE,
                            checked: e.target.checked,
                          })
                        }
                      />
                    </div>
                  </Fragment>
                }
              />

              {state.step === TriggerDataCaptureStep.DENSE && !state.advancedControlsVisible ? (
                <a
                  className={styles.advancedLink}
                  data-test-id="trigger-data-capture-advanced"
                  onClick={() =>
                    dispatch({
                      type:
                        TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_SHOW_ADVANCED_CONTROLS,
                    })
                  }
                >
                  Advanced...
                </a>
              ) : null}
            </div>

            <AppBarContext.Provider value="BOTTOM_ACTIONS">
              <AppBar>
                <AppBarSection />
                <AppBarSection>
                  <ButtonGroup>
                    <Button
                      variant="underline"
                      onClick={() =>
                        dispatch({
                          type:
                            TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CLOSE,
                        })
                      }
                      disabled={state.formDisabled}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="filled"
                      data-test-id="trigger-data-capture-submit"
                      onClick={() => {
                        dispatch({
                          type:
                            TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_SUBMIT,
                          serialNumbers: sensors.map(i => i.serial_number),
                          onComplete,
                        });
                      }}
                      disabled={
                        state.formDisabled ||
                        !customLengthValid ||
                        !numberOfCapturesValid ||
                        !maxCaptureDurationValid
                      }
                    >
                      Trigger
                    </Button>
                  </ButtonGroup>
                </AppBarSection>
              </AppBar>
            </AppBarContext.Provider>
          </Fragment>
        )}
      </Popup>
    </Fragment>
  );
};

export default TriggerDataCaptureButton;
